import _ from "lodash";
import React from "react";
import styled, { css } from "styled-components";

import { Text } from "../Text";

interface IInfoBarItemProps {
  item: IInfoItem;
  listBorderBottom?: boolean;
}

export interface IPopOutProps {
  togglePopOut: () => void;
}

export interface IInfoItem {
  label: string;
  value?: React.ReactChild;
  popOutComponent?(props: IPopOutProps): React.ReactChild | null;
}

/**
 * Info bar item
 */
const InfoBarItem: React.ReactFCWithChildren<IInfoBarItemProps> = props => {
  const [ popOut, togglePopOut ] = React.useState(false);
  const { item, listBorderBottom = false } = props;

  const handleClick = () => {
    if (item.popOutComponent) {
      togglePopOut(!popOut);
    }

    return null;
  };

  return (
    <>
      <Info
        className={item.popOutComponent ? (popOut ? "open" : "closed") : ""}
        onClick={() => handleClick()}
        listBorderBottom={listBorderBottom}
      >
        {item.popOutComponent ? (
          <PopOutLabel>
            <Text>
              {_.startCase(_.toLower(item.label))}
            </Text>
          </PopOutLabel>
        ) : (
          <Label>
            <Text>
              {_.startCase(_.toLower(item.label))}
            </Text>
          </Label>
        )}

        <Value>
          <Text>
            {item.value || "-"}
          </Text>
        </Value>
      </Info>

      {popOut && item.popOutComponent && <PopOut popOutComponent={item.popOutComponent({ togglePopOut: () => togglePopOut(false) })} />}
    </>
  );
};

const PopOut = ({ popOutComponent }: { popOutComponent: React.ReactChild | null }) => {
  return popOutComponent ? (
    <PopOutContainer>
      {popOutComponent}
    </PopOutContainer>
  ) : null;
};

export default InfoBarItem;

const Info = styled.div<{ listBorderBottom: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  ${({ theme, listBorderBottom }) => css`
    padding: ${theme.spacing.padding.sm};
    margin: ${theme.spacing.margins.sm};
    border-bottom: 1px solid ${listBorderBottom ? theme.colors.lightGrey : theme.colors.lightestGrey};
  `};

  &:last-child {
    border-bottom: 0;
  }

  &.open {
    margin-bottom: 0;
    border-bottom: none;
  }

  &.closed,
  &.open {
    cursor: pointer;
  }

  @media (min-width: 768px) {
    &:last-child {
      ${({ theme }) => css`
        border-bottom: 1px solid ${theme.colors.lightestGrey};
      `};
    }
  }
`;

const Label = styled("div")`
  padding-right: 12px;

  &::first-letter {
    text-transform: uppercase;
  }

  span {
    font-weight: ${({ theme }) => theme.fonts.weights.bold};
  }
`;

const PopOutLabel = styled(Label)``;

const Value = styled.div`
  text-align: right;
  white-space: pre-line;
`;

const PopOutContainer = styled.div`
  ${({ theme }) => css`
    background: ${theme.colors.darkGrey};
    color: ${theme.colors.lightestGrey};
    padding: ${theme.spacing.padding.md};
  `};
`;
